import React, { useState, useEffect } from 'react';
import { Plus, Search, Globe, Phone, Building2, MoreVertical, Edit2, Trash2, Users, Download } from 'lucide-react';
import { exportCompanies } from '../utils/export';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../services/api';

const INDUSTRIES = ['SaaS', 'Conseil', 'Finance', 'Industrie', 'Santé', 'Retail', 'Immobilier', 'Technologie', 'Autre'];
const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];

const emptyForm = { name: '', website: '', industry: '', size: '', phone: '', email: '', address: '', notes: '' };

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => toast.error('Erreur chargement')).finally(() => setLoading(false));
  }, []);

  const filtered = companies.filter(c =>
    !search || `${c.name} ${c.industry || ''} ${c.website || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => { setEditingCompany(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (c) => {
    setEditingCompany(c);
    setForm({ name: c.name || '', website: c.website || '', industry: c.industry || '', size: c.size || '', phone: c.phone || '', email: c.email || '', address: c.address || '', notes: c.notes || '' });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      if (editingCompany) {
        const res = await api.put(`/companies/${editingCompany.id}`, form);
        setCompanies(prev => prev.map(c => c.id === editingCompany.id ? res.data : c));
        toast.success('Entreprise mise à jour');
      } else {
        const res = await api.post('/companies', form);
        setCompanies(prev => [res.data, ...prev]);
        toast.success('Entreprise créée');
      }
      setShowDialog(false);
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/companies/${id}`);
      setCompanies(prev => prev.filter(c => c.id !== id));
      toast.success('Entreprise supprimée');
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const getLogoLetter = (name) => name ? name.charAt(0).toUpperCase() : 'E';
  const logoColors = ['#6366F1', '#3B82F6', '#1B99E5', '#F97316', '#EF4444'];
  const getLogoColor = (id) => logoColors[id.charCodeAt(0) % logoColors.length];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Entreprises</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{companies.length} entreprise{companies.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => exportCompanies(filtered)} variant="outline" className="h-9 px-3 text-sm border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="export-companies-btn">
            <Download size={14} className="mr-1.5" /> Exporter CSV
          </Button>
          <Button onClick={openAdd} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-4 text-sm font-medium rounded-lg active:scale-95"
            style={{ transition: 'background-color 150ms ease, transform 100ms ease' }} data-testid="add-company-btn">
            <Plus size={15} className="mr-1.5" /> Nouvelle entreprise
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9 h-9 text-sm border-gray-200 bg-white dark:bg-[#1A1D23]" data-testid="companies-search" />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
              <Building2 size={20} className="text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aucune entreprise</p>
            <Button onClick={openAdd} size="sm" className="mt-3 bg-[#1B99E5] hover:bg-[#0f86cc] text-white">
              <Plus size={13} className="mr-1.5" /> Ajouter
            </Button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Entreprise</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Secteur</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Taille</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Site web</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">Téléphone</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 h-12"
                  style={{ transition: 'background-color 100ms ease' }} data-testid={`company-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                        style={{ backgroundColor: getLogoColor(c.id) }}>
                        {getLogoLetter(c.name)}
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{c.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {c.industry && <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{c.industry}</span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {c.size && <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5"><Users size={12}/>{c.size} employés</span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-sm text-[#3B82F6] hover:underline flex items-center gap-1.5">
                      <Globe size={12}/>{c.website.replace(/^https?:\/\//, '')}
                    </a>}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {c.phone && <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5"><Phone size={12}/>{c.phone}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700" data-testid={`company-actions-${c.id}`}>
                          <MoreVertical size={14} className="text-gray-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(c)}>
                          <Edit2 size={13} className="mr-2" /> Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-600 focus:text-red-600">
                          <Trash2 size={13} className="mr-2" /> Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingCompany ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nom *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Secteur</Label>
                <Select value={form.industry} onValueChange={v => setForm({ ...form, industry: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Taille</Label>
                <Select value={form.size} onValueChange={v => setForm({ ...form, size: v })}>
                  <SelectTrigger><SelectValue placeholder="Employés" /></SelectTrigger>
                  <SelectContent>{SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Téléphone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Site web</Label>
              <Input placeholder="https://..." value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white" data-testid="save-company-button">
              {saving ? 'Sauvegarde...' : editingCompany ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
